// File: webelieve-frontend/src/features/communications/routes/CommunicationsDashboard.tsx
// SMS_07: Super Admin Communications Platform Dashboard (live data)
// Location: System Tools → Communications

import React, { useEffect, useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useAppStore } from '@/store/appStore';
import axios from 'axios';

interface PlatformDashboard {
  activeTenants: number;
  activeNumbers: number;
  messagesToday: { outbound: number; inbound: number; failed: number };
  optOutEventsToday: number;
  tenants: Array<{ id: string; displayName: string; status: string; numberCount: number; onboardedAt: string | null }>;
  recentAudit: Array<{ id: string; action: string; tenantName: string | null; createdAt: string }>;
}

const CommunicationsDashboard: React.FC = () => {
  const setSidebarExpanded = useAppStore((s) => s.setSidebarExpanded);
  useEffect(() => { setSidebarExpanded(false); }, [setSidebarExpanded]);

  const [data, setData] = useState<PlatformDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [providerInfo, setProviderInfo] = useState<{ providerType: string; isLive: boolean } | null>(null);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [dashRes, provRes] = await Promise.all([
        axios.get('/api/v1/communications/admin/dashboard'),
        axios.get('/api/v1/communications/provider/info'),
      ]);
      setData(dashRes.data);
      setProviderInfo(provRes.data);
    } catch (err) {
      console.error('Dashboard fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center mb-2">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-lex-accent mr-3" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Communications Platform</h1>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Multi-tenant SMS communications — Jasmin Gateway + Plivo
            {providerInfo && (
              <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${providerInfo.isLive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'}`}>
                {providerInfo.isLive ? 'Live' : 'Mock Mode'}
              </span>
            )}
          </p>
        </div>
        <button onClick={fetchDashboard} className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
          <ArrowPathIcon className={`h-5 w-5 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card label="Active Tenants" value={String(data?.activeTenants ?? '—')} icon={<UserGroupIcon className="h-5 w-5" />} color="blue" />
        <Card label="Active Numbers" value={String(data?.activeNumbers ?? '—')} icon={<DevicePhoneMobileIcon className="h-5 w-5" />} color="green" />
        <Card label="Sent Today" value={String(data?.messagesToday?.outbound ?? '—')} icon={<ChatBubbleLeftRightIcon className="h-5 w-5" />} color="purple" sub={data ? `${data.messagesToday.inbound} inbound` : undefined} />
        <Card label="Failures Today" value={String(data?.messagesToday?.failed ?? '—')} icon={<ExclamationTriangleIcon className="h-5 w-5" />} color="red" sub={data ? `${data.optOutEventsToday} opt-outs` : undefined} />
      </div>

      {/* Infrastructure */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Infrastructure</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <InfraCard service="Jasmin Gateway" host="AI01:1401" />
          <InfraCard service="RabbitMQ" host="AI01:5672" />
          <InfraCard service="Redis" host="AI01:6381" />
          <InfraCard service="Communications DB" host="AI01:5434" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tenant List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Tenants</h3>
          {data?.tenants.length === 0 && <p className="text-sm text-gray-400">No tenants onboarded yet.</p>}
          <div className="space-y-2">
            {data?.tenants.map(t => (
              <div key={t.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{t.displayName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t.numberCount} number{t.numberCount !== 1 ? 's' : ''}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${t.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                  {t.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Audit */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h3>
          {data?.recentAudit.length === 0 && <p className="text-sm text-gray-400">No recent activity.</p>}
          <div className="space-y-2">
            {data?.recentAudit.slice(0, 10).map(a => (
              <div key={a.id} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                <span className="text-sm text-gray-700 dark:text-gray-300">{a.action.replace(/_/g, ' ')}</span>
                <span className="text-xs text-gray-400">{new Date(a.createdAt).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

function Card({ label, value, icon, color, sub }: { label: string; value: string; icon: React.ReactNode; color: string; sub?: string }) {
  const c: Record<string, string> = {
    blue: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
    green: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
    purple: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20',
    red: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
        <div className={`p-1.5 rounded-lg ${c[color]}`}>{icon}</div>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function InfraCard({ service, host }: { service: string; host: string }) {
  return (
    <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">{service}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{host}</p>
      </div>
    </div>
  );
}

export default CommunicationsDashboard;
